import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'

const useStyles = makeStyles((theme) => ({
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    paddingTop: theme.spacing(0.1),
  },
}))

interface MessageTimestampProps {
  timestamp: number | Date | string
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

const MessageTimestamp: React.FC<MessageTimestampProps> = ({ timestamp }) => {
  const classes = useStyles()
  const messageDate = new Date(timestamp)
  const now = new Date()

  const formatTime = (date: Date): string => {
    return date
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase() // e.g., 3:44 pm
  }

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    )
  }

  // Lógica de formateo
  let formattedDate: string

  // 1. Mensajes del día de hoy
  if (isSameDay(messageDate, now)) {
    formattedDate = formatTime(messageDate) // "3:44 pm"
  } else {
    // 2. Mensajes de esta semana (últimos 7 días, excluyendo hoy)
    const oneWeekAgo = new Date()
    oneWeekAgo.setDate(now.getDate() - 7)
    oneWeekAgo.setHours(0, 0, 0, 0) // Para comparar desde el inicio del día

    const messageDateDayStart = new Date(messageDate)
    messageDateDayStart.setHours(0, 0, 0, 0)

    if (
      messageDateDayStart > oneWeekAgo &&
      messageDateDayStart < new Date(now.setHours(0, 0, 0, 0))
    ) {
      // Excluye hoy que ya se manejó
      const dayName = messageDate.toLocaleDateString('en-US', { weekday: 'long' })
      formattedDate = `${dayName}, ${formatTime(messageDate)}` // "Monday, 3:44 pm"
    } else {
      // 3. Mensajes más antiguos
      const monthName = messageDate.toLocaleDateString('en-US', { month: 'long' })
      const dayOfMonth = messageDate.getDate()
      formattedDate = `${monthName} ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}, ${formatTime(
        messageDate
      )}` // "June 2nd, 3:44 pm"
    }
  }

  now.setTime(Date.now())

  return (
    <Typography variant="caption" className={classes.timestamp}>
      {formattedDate}
    </Typography>
  )
}

export default MessageTimestamp
